/**
 * The speed map: a drive as it was driven, one driver at a time.
 *
 * Opened from "Stats" on a route in the list, and shared from here by link —
 * which is why it sits in front of the sign-in: the people it is sent to have
 * no account, and it is their drive. The trail is the breadcrumbs presence
 * keeps, a point every ten seconds or so with the phone's own GPS speed, drawn
 * over the planned route and coloured either by speed or against the limit of
 * the road, with the figures a group compares afterwards underneath.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  decodePolyline,
  formatDuration,
  parseTrail,
  trailStats,
  withLimits,
  type LngLat,
  type RouteDoc,
  type TrailPoint,
} from '@cad/core';
import { fetchRoute } from '../api';
import { MAPBOX_TOKEN, MAP_STYLE } from '../config';

interface Traces {
  days: number[];
  day: number | null;
  drivers: Array<{ participantId: string; name: string; points: string; updatedAt: number }>;
}

type Mode = 'speed' | 'limit';

const MPH = 2.236936;

/* ─────────────────────────────── colours ─────────────────────────────── */

/**
 * By speed: green through town speeds, into yellow, orange and red, and
 * purple for the properly quick. Blended between the stops so a stretch
 * picking up speed shows it.
 */
const SPEED_STOPS: Array<[number, string]> = [
  [0, '#22c55e'],
  [30, '#22c55e'],
  [40, '#eab308'],
  [50, '#f97316'],
  [60, '#ef4444'],
  [70, '#dc2626'],
  [80, '#a855f7'],
  [100, '#7e22ce'],
];

function speedColour(mph: number): string {
  if (mph <= SPEED_STOPS[0]![0]) return SPEED_STOPS[0]![1];
  for (let i = 1; i < SPEED_STOPS.length; i++) {
    const [top, colour] = SPEED_STOPS[i]!;
    if (mph <= top) {
      const [bottom, from] = SPEED_STOPS[i - 1]!;
      return mix(from, colour, (mph - bottom) / (top - bottom));
    }
  }
  return SPEED_STOPS.at(-1)![1];
}

/** Against the limit: under it, a little over, well over, a lot over; grey where nobody knows the limit. */
function limitColour(mps: number, limitKph: number | null): string {
  if (limitKph === null) return '#9ca3af';
  const ratio = mps / (limitKph / 3.6);
  if (ratio <= 1) return '#22c55e';
  if (ratio <= 1.1) return '#f59e0b';
  if (ratio <= 1.2) return '#ef4444';
  return '#a855f7';
}

function mix(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `#${pa.map((v, i) => Math.round(v + (pb[i]! - v) * t).toString(16).padStart(2, '0')).join('')}`;
}

/* ─────────────────────────────── helpers ─────────────────────────────── */

const clock = (epochSeconds: number) =>
  new Date(epochSeconds * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const dayLabel = (ms: number) =>
  new Date(ms).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const mphText = (mps: number | null) => (mps === null ? '–' : `${Math.round(mps * MPH)} mph`);
const limitMph = (kph: number) => Math.round((kph * 0.621371) / 5) * 5;

/** The trail as short coloured stretches, each coloured by the speed at its start. */
function trailFeatures(points: TrailPoint[], mode: Mode) {
  const features = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p = points[i]!;
    const q = points[i + 1]!;
    // A long gap is a stop or lost signal; joining across it would draw a road nobody drove.
    if (q.t - p.t > 60) continue;
    const mps = p.speedMps ?? 0;
    features.push({
      type: 'Feature' as const,
      properties: {
        colour: mode === 'speed' ? speedColour(mps * MPH) : limitColour(mps, p.limitKph),
        speed: Math.round(mps * MPH),
        limit: p.limitKph === null ? '' : String(limitMph(p.limitKph)),
        time: clock(p.t),
      },
      geometry: { type: 'LineString' as const, coordinates: [p.point, q.point] },
    });
  }
  return { type: 'FeatureCollection' as const, features };
}

function boundsOf(points: LngLat[]): mapboxgl.LngLatBounds | null {
  if (points.length === 0) return null;
  const b = new mapboxgl.LngLatBounds(points[0]!, points[0]!);
  for (const p of points) b.extend(p);
  return b;
}

/* ──────────────────────────────── page ───────────────────────────────── */

export function StatsPage({ routeId, initialDay }: { routeId: string; initialDay: number | null }) {
  const [route, setRoute] = useState<RouteDoc | null>(null);
  const [traces, setTraces] = useState<Traces | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [day, setDay] = useState<number | null>(initialDay);
  const [selected, setSelected] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('speed');
  const [shared, setShared] = useState(false);

  useEffect(() => {
    fetchRoute(routeId)
      .then((r) => {
        setRoute(r);
        document.title = `${r.name} — speeds`;
      })
      .catch(() => setError('That route could not be found.'));
  }, [routeId]);

  useEffect(() => {
    const query = day === null ? '' : `?day=${day}`;
    fetch(`/api/routes/${routeId}/traces${query}`)
      .then((r) => (r.ok ? (r.json() as Promise<Traces>) : Promise.reject(new Error(String(r.status)))))
      .then((t) => {
        setTraces(t);
        setSelected((current) =>
          current && t.drivers.some((d) => d.participantId === current)
            ? current
            : // The fullest trail first: most likely the one worth looking at.
              [...t.drivers].sort((a, b) => b.points.length - a.points.length)[0]?.participantId ?? null,
        );
      })
      .catch(() => setError('The drives for this route could not be loaded.'));
  }, [routeId, day]);

  // One person on two phones is two trails under one name; number the second.
  const labels = useMemo(() => {
    const seen = new Map<string, number>();
    const out = new Map<string, string>();
    for (const d of traces?.drivers ?? []) {
      const n = (seen.get(d.name) ?? 0) + 1;
      seen.set(d.name, n);
      out.set(d.participantId, n === 1 ? d.name : `${d.name} (${n})`);
    }
    return out;
  }, [traces]);

  const driver = traces?.drivers.find((d) => d.participantId === selected) ?? null;
  const points = useMemo(
    () => (driver && route ? withLimits(parseTrail(driver.points), route) : []),
    [driver, route],
  );
  const stats = useMemo(() => trailStats(points), [points]);

  /* ── the map ── */
  const mapNode = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!mapNode.current || map.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const m = new mapboxgl.Map({ container: mapNode.current, style: MAP_STYLE, center: [0.5, 52.5], zoom: 7 });
    m.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
    m.on('load', () => {
      m.addSource('route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#6b7280', 'line-width': 4, 'line-opacity': 0.45 },
      });
      m.addSource('trail', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addLayer({
        id: 'trail-casing',
        type: 'line',
        source: 'trail',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 8 },
      });
      m.addLayer({
        id: 'trail',
        type: 'line',
        source: 'trail',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'colour'], 'line-width': 5 },
      });
      // Tap a stretch for how fast, against what, and when.
      m.on('click', 'trail', (event) => {
        const f = event.features?.[0];
        if (!f) return;
        const { speed, limit, time } = (f as unknown as { properties: unknown }).properties as { speed: number; limit: string; time: string };
        new mapboxgl.Popup({ closeButton: false })
          .setLngLat(event.lngLat)
          .setHTML(`<strong>${speed} mph</strong>${limit ? ` in a ${limit}` : ''}<br><span>${time}</span>`)
          .addTo(m);
      });
      m.on('mouseenter', 'trail', () => (m.getCanvas().style.cursor = 'pointer'));
      m.on('mouseleave', 'trail', () => (m.getCanvas().style.cursor = ''));
      setMapReady(true);
    });
    map.current = m;
    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  // The planned route, once.
  useEffect(() => {
    const m = map.current;
    if (!mapReady || !m || !route) return;
    (m.getSource('route') as mapboxgl.GeoJSONSource).setData({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: decodePolyline(route.geometry) },
    });
  }, [mapReady, route]);

  // The chosen driver's trail, in the chosen colours; framed on it.
  useEffect(() => {
    const m = map.current;
    if (!mapReady || !m) return;
    (m.getSource('trail') as mapboxgl.GeoJSONSource).setData(trailFeatures(points, mode));
  }, [mapReady, points, mode]);

  useEffect(() => {
    const m = map.current;
    if (!mapReady || !m) return;
    const frame =
      boundsOf(points.map((p) => p.point)) ?? (route ? boundsOf(decodePolyline(route.geometry)) : null);
    if (frame) m.fitBounds(frame, { padding: 40, duration: 0, maxZoom: 14 });
    // Framed when the driver changes, not when the colours do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, selected, route]);

  /* ── sharing ── */
  const share = async () => {
    const url = `${window.location.origin}/?stats=${routeId}${traces?.day ? `&day=${traces.day}` : ''}`;
    const title = route ? `${route.name} — speeds` : 'Speeds';
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch {
      // Cancelled share sheet: nothing to do.
    }
  };

  if (error) return <div className="stats-page"><p className="stats-empty">{error}</p></div>;

  return (
    <div className="stats-page">
      <header className="stats-header">
        <div className="stats-title">
          <h1>{route?.name ?? 'Loading…'}</h1>
          {traces && traces.days.length > 0 && (
            traces.days.length > 1 ? (
              <select
                className="stats-day"
                value={traces.day ?? ''}
                onChange={(e) => setDay(Number(e.target.value))}
              >
                {traces.days.map((d) => (
                  <option key={d} value={d}>{dayLabel(d)}</option>
                ))}
              </select>
            ) : (
              <span className="stats-day-label">{traces.day ? dayLabel(traces.day) : ''}</span>
            )
          )}
        </div>
        <button type="button" className="primary-button stats-share" onClick={share}>
          {shared ? 'Link copied' : 'Share'}
        </button>
      </header>

      {traces && traces.drivers.length > 0 && (
        <div className="stats-drivers" role="tablist">
          {traces.drivers.map((d) => (
            <button
              key={d.participantId}
              type="button"
              role="tab"
              aria-selected={d.participantId === selected}
              className={`stats-driver${d.participantId === selected ? ' is-selected' : ''}`}
              onClick={() => setSelected(d.participantId)}
            >
              {labels.get(d.participantId) ?? d.name}
            </button>
          ))}
        </div>
      )}

      <div className="stats-controls">
        <div className="stats-mode" role="radiogroup" aria-label="Colour the trail by">
          <button type="button" className={mode === 'speed' ? 'is-selected' : ''} onClick={() => setMode('speed')}>
            Speed
          </button>
          <button type="button" className={mode === 'limit' ? 'is-selected' : ''} onClick={() => setMode('limit')}>
            Against the limit
          </button>
        </div>
        <Legend mode={mode} />
      </div>

      <div className="stats-map" ref={mapNode} />

      {traces && traces.drivers.length === 0 && (
        <p className="stats-empty">
          No drives recorded on this route yet. They appear here as people drive it, and are kept for 48
          hours.
        </p>
      )}

      {driver && (
        <section className="stats-figures" aria-label={`${driver.name}'s drive`}>
          <Figure label="Distance" value={`${(stats.distanceMeters / 1609.344).toFixed(1)} mi`} />
          <Figure label="Moving" value={formatDuration(stats.movingSeconds)} />
          <Figure label="Average" value={mphText(stats.averageMps)} />
          <Figure label="Top speed" value={mphText(stats.topSpeedMps)} />
          <Figure label="Over the limit" value={formatDuration(stats.overLimitSeconds)} />
          <Figure
            label="Worst moment"
            value={
              stats.worstOver
                ? `${Math.round(stats.worstOver.speedMps * MPH)} in a ${limitMph(stats.worstOver.limitKph)}`
                : 'None'
            }
            detail={stats.worstOver ? clock(stats.worstOver.t) : undefined}
          />
          {stats.startedAt !== null && stats.endedAt !== null && (
            <Figure label="Recorded" value={`${clock(stats.startedAt)}–${clock(stats.endedAt)}`} />
          )}
        </section>
      )}
    </div>
  );
}

function Figure({ label, value, detail }: { label: string; value: string; detail?: string | undefined }) {
  return (
    <div className="stats-figure">
      <span className="stats-figure-label">{label}</span>
      <span className="stats-figure-value">{value}</span>
      {detail && <span className="stats-figure-detail">{detail}</span>}
    </div>
  );
}

function Legend({ mode }: { mode: Mode }) {
  const keys =
    mode === 'speed'
      ? [
          ['#22c55e', '≤30'],
          ['#eab308', '40'],
          ['#f97316', '50'],
          ['#ef4444', '60–70'],
          ['#a855f7', '80+'],
        ]
      : [
          ['#22c55e', 'Under'],
          ['#f59e0b', '+10%'],
          ['#ef4444', '+20%'],
          ['#a855f7', 'More'],
          ['#9ca3af', 'No limit known'],
        ];
  return (
    <div className="stats-legend">
      {keys.map(([colour, text]) => (
        <span key={text}>
          <i style={{ background: colour }} /> {text}
        </span>
      ))}
      {mode === 'speed' && <span className="stats-legend-unit">mph</span>}
    </div>
  );
}
